import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NowCompactProductCard } from '../now/NowCompactProductCard';
import { ShoppingCartIcon, MinusIcon, PlusIcon, ClockIcon, XIcon, HeartIcon, StarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { GlobalBadge } from './GlobalBadge';
import { SmartImage } from './SmartImage';
import { haptics } from '../../lib/utils/haptics';
import { useStore } from '../../lib/store';
import { motion, AnimatePresence } from 'framer-motion';
import { useHorizontalScroll } from '../../lib/hooks/useHorizontalScroll';
interface EnhancedProductCardProps {
  product: {
    id: number;
    name: string;
    brand?: string;
    price: number;
    originalPrice?: number;
    discount?: number;
    rating?: number;
    reviewCount?: number;
    image: string;
    isBestSeller?: boolean;
    isNew?: boolean;
    expressDelivery?: boolean;
    isGlobal?: boolean;
    endingSoon?: boolean;
  };
}
export function EnhancedProductCard({
  product
}: EnhancedProductCardProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNowDealsContext = window.location.pathname === '/offers' && searchParams.get('from') === 'now';
  const {
    cart,
    addToCart,
    updateQuantity,
    toggleWishlist,
    isInWishlist
  } = useStore();
  const cartItem = cart.find(item => item.id === product.id);
  const quantity = cartItem?.quantity || 0;
  const isWishlisted = isInWishlist(product.id);
  const [showDetailsSheet, setShowDetailsSheet] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const displayDiscount = product.discount || (product.originalPrice ? Math.round((product.originalPrice - product.price) / product.originalPrice * 100) : null);
  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.success();
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      brand: product.brand
    });
    toast.custom(t => <motion.div initial={{
      opacity: 0,
      y: 50,
      scale: 0.9
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.9
    }} className="flex min-w-[320px] items-center gap-4 rounded-2xl border-2 border-brand bg-white p-4 shadow-2xl" data-id="element-5377" data-mp="ybfquh0sqk3x">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-brand-light" data-id="element-5378" data-mp="fcto6xpmskxw">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          damping: 12
        }} data-id="element-5379" data-mp="op2rlz71vbf">
              <ShoppingCartIcon className="h-6 w-6 text-brand" data-id="element-5380" data-mp="3rsdrz6aa5ueq" />
            </motion.div>
          </div>
          <div className="flex-1" data-id="element-5381" data-mp="395xv2hvzy2uu">
            <p className="text-sm font-bold text-gray-900" data-id="element-5382" data-mp="39790em53bio2">Added to Cart!</p>
            <p className="text-xs text-gray-500 line-clamp-1" data-id="element-5383" data-mp="347mn6cp9i2f9">{product.name}</p>
          </div>
          <button onClick={() => {
        toast.dismiss(t);
        navigate('/cart');
      }} className="bg-[#1E2B55] text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#162041] transition-colors" data-id="element-5384" data-mp="3i7e68rumy904">
            View Cart
          </button>
        </motion.div>, {
      duration: 3000
    });
  };
  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.light();
    updateQuantity(product.id, quantity + 1);
  };
  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.light();
    updateQuantity(product.id, quantity - 1);
  };
  const handleWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptics.selection();
    toggleWishlist({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      brand: product.brand
    });
  };
  const handleCardClick = () => {
    haptics.impact();
    if (isMobile) {
      setShowDetailsSheet(true);
    } else {
      navigate(`/product/${product.id}`);
    }
  };
  if (isNowDealsContext) {
    return <NowCompactProductCard product={product} data-id="element-5385" data-mp="at9puudvkwpf" />;
  }
  return <>
      <div onClick={handleCardClick} className="bg-white rounded-lg overflow-hidden active:scale-[0.98] transition-all group cursor-pointer h-full flex flex-col border border-gray-100" data-id="element-5386" data-mp="av2wi9ohngxf">
        {/* Product Image */}
        <div className="relative aspect-[4/5] bg-gray-50 overflow-hidden flex-shrink-0" data-id="element-5387" data-mp="osrl9slof87r">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-5388" data-mp="1v93k7vwbou4e" />

          {/* Ending Soon Badge */}
          {product.endingSoon && <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-red-50 text-red-600 border border-red-100 text-[10px] font-semibold px-2 py-1 rounded-md flex items-center gap-1 whitespace-nowrap z-10" data-id="element-5389" data-mp="3kph2odzgq1ga">
              <ClockIcon className="w-3 h-3 animate-pulse" data-id="element-5390" data-mp="3ay2t1ddsrl9f" />
              ENDING SOON
            </div>}

          {/* Top-left: discount / best seller / new */}
          <div className="absolute top-2 left-2 flex flex-col gap-1.5 items-start z-10" data-id="element-5391" data-mp="321c039ujkmj2">
            {product.discount && <div className="bg-red-50 text-red-600 border border-red-100 text-[10px] font-semibold px-2 py-0.5 rounded-md" data-id="element-5392" data-mp="2x30pgwwfa7zs">
                -{product.discount}%
              </div>}
            {product.isBestSeller && <div className="bg-yellow-50 text-yellow-700 border border-yellow-100 text-[10px] font-semibold px-2 py-0.5 rounded-md" data-id="element-5393" data-mp="qagfigjp5f0r">
                Best Seller
              </div>}
            {product.isNew && <div className="bg-green-50 text-green-700 border border-green-100 text-[10px] font-semibold px-2 py-0.5 rounded-md" data-id="element-5394" data-mp="2fpr6aohowfvo">
                New
              </div>}
          </div>

          {/* Floating Wishlist Button */}
          <button onClick={handleWishlist} className="absolute top-2 right-2 w-8 h-8 rounded-md bg-white/80 backdrop-blur-sm flex items-center justify-center transition-colors border border-gray-100 z-10" data-id="element-5395" data-mp="2as8vsto0xslk">
            <HeartIcon className={`w-4 h-4 ${isWishlisted ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} data-id="element-5396" data-mp="33flh70ig7uda" />
          </button>
        </div>
        {/* Product Info */}
        <div className="p-2 flex flex-col flex-grow" data-id="element-5397" data-mp="2thffok0k0q8y">
          {/* Top Row: Rating & Add to Cart */}
          <div className="flex items-center justify-between mb-2 min-h-[32px]" data-id="element-5398" data-mp="1e16xiumaexon">
            {/* Rating */}
            <div className="flex items-center gap-1 text-gray-400 text-[10px]" data-id="element-5399" data-mp="1i54w947pa6h6">
              {product.rating !== undefined && product.reviewCount !== undefined && product.reviewCount > 0 ? <>
                  <span className="font-bold text-gray-600" data-id="element-5400" data-mp="1ki8hcb2kcpnx">
                    {product.rating}
                  </span>
                  <StarIcon className="w-2.5 h-2.5 fill-yellow-400 text-yellow-400" data-id="element-5401" data-mp="1fivrrikb54hn" />
                  <span className="opacity-80" data-id="element-5402" data-mp="rtb5oqsuxr45">({product.reviewCount})</span>
                </> : <span className="opacity-0" data-id="element-5403" data-mp="mv9etjp6wni4">0.0</span>}
            </div>

            {/* Add to Cart Button */}
            <div className="relative" data-id="element-5404" data-mp="11kxgim1a9o6d">
              <AnimatePresence mode="wait" data-id="element-5405" data-mp="wsntpevgt48b">
                {quantity === 0 ? <motion.button key="add-to-cart" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} whileTap={{
                scale: 0.95
              }} onClick={handleAddToCart} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-700 hover:border-[#1E2B55] hover:text-[#1E2B55] transition-colors" data-id="element-5406" data-mp="wqkv46g8y2rz">
                    <div className="relative" data-id="element-5407" data-mp="6h1kmz8hg0zf">
                      <ShoppingCartIcon className="w-4 h-4" strokeWidth={1.5} data-id="element-5408" data-mp="b9ky7w766w48" />
                      <PlusIcon className="w-2.5 h-2.5 absolute -bottom-1 -right-1 bg-white rounded-sm text-gray-900" strokeWidth={2.5} data-id="element-5409" data-mp="bbp0yatc1p2x" />
                    </div>
                  </motion.button> : <motion.div key="quantity-stepper" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} className="h-8 bg-[#1E2B55] text-white rounded-lg flex items-center justify-between overflow-hidden shadow-sm px-1" onClick={e => e.stopPropagation()} data-id="element-5410" data-mp="p8uf6y379u99">
                    <button onClick={handleDecrement} className="w-6 h-full flex items-center justify-center active:bg-[#162041] transition-colors rounded-md" aria-label="Decrease quantity" data-id="element-5411" data-mp="2ogkmdywudbmy">
                      <MinusIcon className="w-3 h-3" data-id="element-5412" data-mp="3c6oo20qgz1bb" />
                    </button>
                    <motion.span key={quantity} initial={{
                  scale: 1.2,
                  fontWeight: 700
                }} animate={{
                  scale: 1,
                  fontWeight: 700
                }} className="text-xs w-4 text-center" data-id="element-5413" data-mp="37edc1zigfp2e">
                      {quantity}
                    </motion.span>
                    <button onClick={handleIncrement} className="w-6 h-full flex items-center justify-center active:bg-[#162041] transition-colors rounded-md" aria-label="Increase quantity" data-id="element-5414" data-mp="1b0kmr61ze0eg">
                      <PlusIcon className="w-3 h-3" data-id="element-5415" data-mp="1ksgn6nrji2lu" />
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Brand */}
          {product.brand && <p className="text-[10px] text-gray-400 mb-0.5 font-medium" data-id="element-5416" data-mp="1otab3h4zfheq">
              {product.brand}
            </p>}
          {/* Product Name */}
          <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-2 leading-tight group-hover:text-[#1E2B55] transition-colors flex-grow" data-id="element-5417" data-mp="2dd3v7edflhzt">
            {product.name}
          </h3>
          
          {/* Price */}
          <div className="flex flex-col mt-auto" data-id="element-5418" data-mp="23e6bqpuv4eno">
            {/* Old Price & Discount */}
            <div className="flex items-center gap-2 min-h-[18px] mb-0.5" data-id="element-5419" data-mp="1pn18ldsad48l">
              {product.originalPrice && <>
                  <span className="text-[10px] text-gray-400 line-through" data-id="element-5420" data-mp="1ufualtwddygz">
                    {product.originalPrice} SAR
                  </span>
                  {displayDiscount && <span className="bg-red-100 text-red-600 text-[10px] font-bold px-1.5 py-0.5 rounded-md" data-id="element-5421" data-mp="1kp83ko2pyg9w">
                      -{displayDiscount}%
                    </span>}
                </>}
            </div>
            {/* Final Price */}
            <span className={`text-base font-bold ${product.originalPrice ? 'text-red-500' : 'text-gray-900'}`} data-id="element-5422" data-mp="3dd7ceovtfd8z">
              {product.price} SAR
            </span>
          </div>
        </div>
      </div>

      {/* Mobile Details Bottom Sheet */}
      <AnimatePresence data-id="element-5423" data-mp="2zlstsj5tle2g">
        {showDetailsSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={e => {
          e.stopPropagation();
          setShowDetailsSheet(false);
        }} className="fixed inset-0 bg-black/50 z-[100] md:hidden" data-id="element-5424" data-mp="2zjg5zpdds9hh" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[101] md:hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-5425" data-mp="2bw8b81dkwitp">
              <div className="flex justify-center pt-3 pb-2 flex-shrink-0" data-id="element-5426" data-mp="108c482hbhn6w">
                <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-5427" data-mp="154st4ywcc5l1" />
              </div>

              <div className="flex justify-between items-center px-4 pb-2 border-b border-gray-100 flex-shrink-0" data-id="element-5428" data-mp="1a34277etac27">
                <h3 className="font-bold text-lg" data-id="element-5429" data-mp="1f49wvas189ed">Deal Details</h3>
                <button onClick={() => setShowDetailsSheet(false)} className="p-2 hover:bg-gray-100 rounded-full" data-id="element-5430" data-mp="1j5d97navfnau">
                  <XIcon className="w-5 h-5" data-id="element-5431" data-mp="3kwgl0fwhz7wz" />
                </button>
              </div>

              <div className="overflow-y-auto p-4 flex-grow" data-id="element-5432" data-mp="2pma3abyoxdv3">
                <div className="aspect-square bg-gray-50 rounded-xl overflow-hidden mb-4 relative" data-id="element-5433" data-mp="2pkp9wpo440yt">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-5434" data-mp="2lk59nd88sh8g" />
                  {product.discount && <div className="absolute top-3 left-3 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full" data-id="element-5435" data-mp="2ujk0wew8gvu0">
                      -{product.discount}%
                    </div>}
                </div>

                {product.brand && <p className="text-sm text-gray-500 uppercase tracking-wide mb-1" data-id="element-5436" data-mp="2gn6jt6vm2wg2">
                    {product.brand}
                  </p>}
                <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-5437" data-mp="1o4rf3tby54ux">
                  {product.name}
                </h2>

                <div className="bg-red-50 rounded-xl p-4 mb-6 border border-red-100" data-id="element-5438" data-mp="1j5euzgcgmbw1">
                  <div className="flex justify-between items-end" data-id="element-5439" data-mp="3utbexdl433yl">
                    <div data-id="element-5440" data-mp="3evtf8s57h8c">
                      <p className="text-sm text-red-600 font-medium mb-1" data-id="element-5441" data-mp="3f5jewd6toe1">
                        Special Deal Price
                      </p>
                      <div className="flex items-baseline gap-2" data-id="element-5442" data-mp="3lqsawq5lalar">
                        <span className="text-3xl font-bold text-red-600" data-id="element-5443" data-mp="3pvi3o64ybjrg">
                          {product.price}
                        </span>
                        <span className="text-red-600 font-medium" data-id="element-5444" data-mp="hcu2pzrlwngz">SAR</span>
                      </div>
                    </div>
                    {product.originalPrice && <div className="text-right" data-id="element-5445" data-mp="8dp4yvnvcy5v">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5446" data-mp="dca9v8e2g60n">
                          Regular Price
                        </p>
                        <span className="text-lg text-gray-400 line-through" data-id="element-5447" data-mp="2t2mrq7u6p4d1">
                          {product.originalPrice} SAR
                        </span>
                      </div>}
                  </div>
                </div>

                <div className="space-y-4" data-id="element-5448" data-mp="ynhatyk8phy4">
                  <button onClick={() => {
                navigate(`/product/${product.id}`);
                setShowDetailsSheet(false);
              }} className="w-full bg-gray-100 text-gray-900 font-bold py-3.5 rounded-xl" data-id="element-5449" data-mp="yorb8uekf27x">
                    View Full Product Details
                  </button>

                  <button onClick={e => {
                handleAddToCart(e);
                setShowDetailsSheet(false);
              }} className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3.5 font-bold text-white shadow-lg shadow-brand/20 transition-colors duration-150 hover:bg-brand-dark" data-id="element-5450" data-mp="fwq474rn05nf">
                    <ShoppingCartIcon className="w-5 h-5" data-id="element-5451" data-mp="21c5o04gxbvrh" />
                    {quantity > 0 ? 'Added to Cart' : 'Add to Cart'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}